// src/components/CoupleForm.js
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { normalizeDancer, formatDancerForAPI } from '../utils/fieldCompatibility';
import './CoupleForm.css';
import CountrySelector from './CountrySelector';

//const _host = 'chappie-demo.novosense.africa';
const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const CoupleForm = ({ existingCouple, onCoupleSubmitted, onCancel, onExternalSubmit }) => {
  const { selectedCompetition } = useCompetition();
  const [clothNumber, setClothNumber] = useState('');
  const [dancerType, setDancerType] = useState('couple'); // 'couple', 'solo', 'team'
  const [leadName, setLeadName] = useState('');
  const [followName, setFollowName] = useState('');
  const [teamMembers, setTeamMembers] = useState(['', '', '', '', '']); // 5 team members max
  const [country, setCountry] = useState('South Africa');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingNumbers, setExistingNumbers] = useState([]);
  const [generatingNumber, setGeneratingNumber] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [clubsLoading, setClubsLoading] = useState(false);

  // Fetch existing couple numbers
  const fetchExistingNumbers = async () => {
    try {
      let url = `https://${_host}:6003/api/${_company}/couples`;

      // Add competitionId filter if available
      if (selectedCompetition && selectedCompetition.id) {
        url += `?competitionId=${selectedCompetition.id}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const couples = await response.json();
        const numbers = couples.map(couple => couple.clothNumber);
        setExistingNumbers(numbers);
      }
    } catch (err) {
      console.error('Error fetching existing numbers:', err);
    }
  };

  // Fetch clubs from API
  const fetchClubs = async () => {
    try {
      setClubsLoading(true);
      console.log('Fetching clubs...');
      let url = `https://${_host}:6003/api/${_company}/clubs`;

      // Try without competition filtering first
      const response = await fetch(url);
      if (response.ok) {
        const clubsData = await response.json();
        console.log('Clubs fetched successfully:', clubsData.length, 'clubs found');
        setClubs(clubsData);
      } else {
        console.error('Failed to fetch clubs, status:', response.status);
        // Try with competition filtering as fallback
        if (selectedCompetition && selectedCompetition.id) {
          const urlWithComp = `${url}?competitionId=${selectedCompetition.id}`;
          const compResponse = await fetch(urlWithComp);
          if (compResponse.ok) {
            const clubsData = await compResponse.json();
            console.log('Clubs fetched with competition filter:', clubsData.length, 'clubs found');
            setClubs(clubsData);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching clubs:', err);
      setClubs([]); // Set empty array on error to prevent issues
    } finally {
      setClubsLoading(false);
    }
  };

  // Initialize form with existing data if editing
  useEffect(() => {
    fetchExistingNumbers();
    fetchClubs();

    if (existingCouple) {
      // Normalize the couple data for consistent field access
      const normalizedCouple = normalizeDancer(existingCouple);
      console.log('Editing existing couple:', normalizedCouple);
      setClothNumber(normalizedCouple.clothNumber || '');
      setLeadName(normalizedCouple.leadName || '');
      setFollowName(normalizedCouple.followName || '');
      setCountry(normalizedCouple.country || 'South Africa');
      setSelectedClub(normalizedCouple.clubId || '');

      // Determine dancer type based on existing data
      if (normalizedCouple.teamMembers && normalizedCouple.teamMembers.length > 0) {
        setDancerType('team');
        const members = [...normalizedCouple.teamMembers];
        while (members.length < 5) members.push(''); // Pad to 5 members
        setTeamMembers(members);
      } else if (normalizedCouple.followName) {
        setDancerType('couple');
      } else {
        setDancerType('solo');
      }
    } else {
      // Reset to default values for new couple
      setCountry('South Africa');
      setSelectedClub('');
    }
  }, [existingCouple]);

  // Log when existingCouple changes
  useEffect(() => {
    if (existingCouple) {
      console.log('Editing couple changed:', existingCouple);
    }
  }, [existingCouple]);

  // Re-fetch clubs when competition changes
  useEffect(() => {
    if (selectedCompetition) {
      fetchClubs();
    }
  }, [selectedCompetition]);

  // Generate random cloth number
  const generateRandomNumber = async () => {
    setGeneratingNumber(true);
    
    try {
      // Refresh existing numbers first
      await fetchExistingNumbers();
      
      let attempts = 0;
      let randomNumber;
      
      do {
        // Generate random number between 1-999
        const num = Math.floor(Math.random() * 999) + 1;
        // Format with leading zeros (001, 010, 100, etc.)
        randomNumber = num.toString().padStart(3, '0');
        attempts++;
        
        // Prevent infinite loop
        if (attempts > 1000) {
          throw new Error('Unable to generate unique number. All numbers may be taken.');
        }
      } while (existingNumbers.includes(randomNumber));
      
      setClothNumber(randomNumber);
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingNumber(false);
    }
  };

  // Handle team member name changes
  const handleTeamMemberChange = (index, value) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers[index] = value;
    setTeamMembers(newTeamMembers);
  };

  // Handle dancer type change
  const handleDancerTypeChange = (type) => {
    setDancerType(type);
    // Reset relevant fields when type changes
    if (type === 'solo') {
      setFollowName('');
      setTeamMembers(['', '', '', '', '']);
    } else if (type === 'couple') {
      setTeamMembers(['', '', '', '', '']);
    } else if (type === 'team') {
      setLeadName('');
      setFollowName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate inputs
      if (!clothNumber.trim()) {
        throw new Error('Couple number is required');
      }

      if (isNaN(parseInt(clothNumber))) {
        throw new Error('Couple number must be a valid number');
      }

      if (!country) {
        throw new Error('Country is required');
      }

      // Create couple data object based on dancer type
      let coupleData = {
        clothNumber: clothNumber.trim(),
        country: country,
        dancerType: dancerType,
        clubId: selectedClub
      };

      // Validate and add names based on dancer type
      if (dancerType === 'solo') {
        if (!leadName.trim()) {
          throw new Error('Dancer name is required');
        }
        coupleData.leadName = leadName.trim();
        coupleData.name = leadName.trim();
      } else if (dancerType === 'couple') {
        if (!leadName.trim() || !followName.trim()) {
          throw new Error('Both lead and follow names are required');
        }
        coupleData.leadName = leadName.trim();
        coupleData.followName = followName.trim();
        coupleData.name = `${leadName.trim()} & ${followName.trim()}`;
      } else if (dancerType === 'team') {
        const filledMembers = teamMembers.filter(member => member.trim() !== '');
        if (filledMembers.length < 2) {
          throw new Error('Team must have at least 2 members');
        }
        coupleData.teamMembers = filledMembers;
        coupleData.name = filledMembers.join(', ');
        coupleData.leadName = filledMembers[0]; // First member as lead for compatibility
      }

      // Add competitionId if a competition is selected
      if (selectedCompetition && selectedCompetition.id) {
        coupleData.competitionId = selectedCompetition.id;
      }

      // Format couple data for API submission with both field naming conventions
      const formattedCoupleData = formatDancerForAPI(coupleData);

      let response;

      // Update existing couple or create new one
      if (existingCouple) {
        response = await fetch(`https://${_host}:6003/api/${_company}/couples/${existingCouple.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formattedCoupleData)
        });
      } else {
        response = await fetch(`https://${_host}:6003/api/${_company}/couples`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formattedCoupleData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save dancer(s)');
      }

      // Call the parent callback with the saved couple
      const savedCouple = await response.json();
      onCoupleSubmitted(savedCouple);
      
      // Reset form if adding a new couple
      if (!existingCouple) {
        setClothNumber('');
        setLeadName('');
        setFollowName('');
        setTeamMembers(['', '', '', '', '']);
        setCountry('South Africa');
        setDancerType('couple');
      }
    } catch (err) {
      console.error('Error saving dancer(s):', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="couple-form-container">
      <h3>{existingCouple ? 'Edit Dancer(s)' : 'Add New Dancer(s)'}</h3>
      
      {error && <div className="error-message">{error}</div>}
      
      <form className="couple-form" onSubmit={handleSubmit}>
        {/* Dancer Type Selection */}
        <div className="form-group dancer-type-group">
          <label>Dancer Type:</label>
          <div className="dancer-type-options">
            <label className="radio-option">
              <input
                type="radio"
                name="dancerType"
                value="solo"
                checked={dancerType === 'solo'}
                onChange={(e) => handleDancerTypeChange(e.target.value)}
                disabled={loading}
              />
              Solo
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="dancerType"
                value="couple"
                checked={dancerType === 'couple'}
                onChange={(e) => handleDancerTypeChange(e.target.value)}
                disabled={loading}
              />
              Couple/Duo
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="dancerType"
                value="team"
                checked={dancerType === 'team'}
                onChange={(e) => handleDancerTypeChange(e.target.value)}
                disabled={loading}
              />
              Team (2-5 members)
            </label>
            </div>
        </div>

        {/* Cloth Number with Random Generator */}
        <div className="form-group">
          <label htmlFor="clothNumber">
            {dancerType === 'solo' ? 'Dancer' : dancerType === 'couple' ? 'Couple' : 'Team'} Number:
          </label>
          <div className="cloth-number-input">
            <input
              type="text"
              id="clothNumber"
              name="clothNumber"
              value={clothNumber}
              onChange={(e) => setClothNumber(e.target.value)}
              placeholder="e.g., 001"
              required
              disabled={loading}
              maxLength={3}
            />
            <button
              type="button"
              className="random-number-btn"
              onClick={generateRandomNumber}
              disabled={loading || generatingNumber}
              title="Generate random number"
            >
              {generatingNumber ? '🎲' : '🎲'}
            </button>
          </div>
          <small className="helper-text">Format: 001-999</small>
        </div>

        {/* Solo Dancer */}
        {dancerType === 'solo' && (
          <div className="form-group">
            <label htmlFor="leadName">Dancer Name:</label>
            <input
              type="text"
              id="leadName"
              name="leadName"
              value={leadName}
              onChange={(e) => setLeadName(e.target.value)}
              placeholder="Dancer's full name"
              required
              disabled={loading}
            />
          </div>
        )}

        {/* Couple/Duo */}
        {dancerType === 'couple' && (
          <>
            <div className="form-group">
              <label htmlFor="leadName">Lead Dancer Name:</label>
              <input
                type="text"
                id="leadName"
                name="leadName"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                placeholder="Lead dancer's full name"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="followName">Follow Dancer Name:</label>
              <input
                type="text"
                id="followName"
                name="followName"
                value={followName}
                onChange={(e) => setFollowName(e.target.value)}
                placeholder="Follow dancer's full name"
                required
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* Team Members */}
        {dancerType === 'team' && (
          <div className="team-members-group">
            <label>Team Members (2-5 required):</label>
            {teamMembers.map((member, index) => (
              <div key={index} className="form-group">
                <label htmlFor={`member${index}`}>Member {index + 1}:</label>
                <input
                  type="text"
                  id={`member${index}`}
                  value={member}
                  onChange={(e) => handleTeamMemberChange(index, e.target.value)}
                  placeholder={index < 2 ? "Required member name" : "Optional member name"}
                  required={index < 2}
                  disabled={loading}
                />
              </div>
            ))}
          </div>
        )}
        
        {/* Country with Search */}
        <div className="form-group">
          <label htmlFor="country">Country:</label>
          <CountrySelector
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={loading}
            required={true}
            searchable={true}
            placeholder="Search and select country"
          />
        </div>

        {/* Club/Studio Selection */}
        <div className="form-group">
          <label htmlFor="club">Club/Studio:</label>
          <div className="club-selection-wrapper">
            <select
              id="club"
              value={selectedClub}
              onChange={(e) => setSelectedClub(e.target.value)}
              disabled={loading || clubsLoading}
              className="club-select"
            >
              <option value="">
                {clubsLoading ? 'Loading clubs...' : 'Select a club/studio (optional)'}
              </option>
              {!clubsLoading && clubs.map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={fetchClubs}
              disabled={loading || clubsLoading}
              className="refresh-clubs-btn"
              title="Refresh clubs list"
            >
              🔄
            </button>
          </div>
          {!clubsLoading && clubs.length === 0 && (
            <small className="form-help-text">
              No clubs available. Add clubs in the Clubs tab first.
            </small>
          )}
          {clubsLoading && (
            <small className="form-help-text">
              Loading clubs from server...
            </small>
          )}
        </div>

        <div className="form-buttons">
          <button
            type="button"
            className="save-couple-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : `Save ${dancerType === 'solo' ? 'Dancer' : dancerType === 'couple' ? 'Couple' : 'Team'}`}
          </button>

          <button
            type="button"
            className="cancel-btn"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CoupleForm;